import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { faqSummary } from '../data/faqPlatformFaqData';
import './SubmitFaqPanel.css';

// The upload form and submission history shown on the FAQ Platform page's
// "Submit" tab. It is a panel rather than a page: access is whatever the FAQ
// Platform page itself requires, so there is no separate permission to grant
// and no page chrome of its own (the host page supplies the header and tabs).

// Kept in step with ACCEPTED in backend/routes/faqSubmissions.js. The server is
// the authority — it also checks the file's leading signature — but matching the
// list here lets the picker filter and lets an obviously wrong file be rejected
// before it is uploaded.
const ACCEPTED_EXTENSIONS = ['.docx', '.doc', '.pdf'];
const MAX_BYTES = 15 * 1024 * 1024;

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatWhen = (ts) => {
  try {
    return new Date(ts).toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return '';
  }
};

function SubmitFaqPanel() {
  const { user } = useAuth();

  const [faqName, setFaqName] = useState('');
  const [version, setVersion] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);

  const [submissions, setSubmissions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const fileInputRef = useRef(null);

  const loadSubmissions = useCallback(async () => {
    try {
      const res = await fetch('/api/faq-submissions');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load submissions.');
      setSubmissions(data.submissions || []);
      setIsAdmin(Boolean(data.isAdmin));
      // Offer the FAQ name the petitioner used last time, so a routine update
      // is just "pick file, submit".
      setFaqName(prev => prev || (data.isAdmin ? '' : (data.submissions?.[0]?.faq_name || '')));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  // Shared by the file picker and the drop zone.
  const acceptFile = (candidate) => {
    setStatus('');
    if (!candidate) return;
    const name = candidate.name.toLowerCase();
    if (!ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext))) {
      setError(`Please choose a ${ACCEPTED_EXTENSIONS.join(', ')} file.`);
      setFile(null);
      return;
    }
    if (candidate.size > MAX_BYTES) {
      setError(`That file is ${formatSize(candidate.size)}. The limit is 15 MB.`);
      setFile(null);
      return;
    }
    setError('');
    setFile(candidate);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setStatus('');

    if (!faqName.trim()) { setError('Please give the name of the FAQ.'); return; }
    if (!file) { setError('Please choose the document to submit.'); return; }

    setUploading(true);
    try {
      const params = new URLSearchParams({ faqName: faqName.trim() });
      if (version.trim()) params.set('version', version.trim());
      if (note.trim()) params.set('note', note.trim());

      // The document is sent as the raw request body with its name in a header;
      // the backend reads it with express.raw rather than a multipart parser.
      const res = await fetch(`/api/faq-submissions?${params.toString()}`, {
        method: 'POST',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'X-Filename': file.name,
        },
        body: file,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'The upload failed.');

      setStatus(
        data.emailed
          ? 'Submitted. Michael has been emailed a copy and will follow up.'
          : 'Submitted and saved. The notification email could not be sent, so please also let Michael know directly.'
      );
      setFile(null);
      setVersion('');
      setNote('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadSubmissions();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (submission) => {
    if (!window.confirm(`Delete "${submission.filename}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/faq-submissions/${submission.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not delete that submission.');
      setStatus(`Deleted ${data.deleted}.`);
      loadSubmissions();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <>
      <section className="submit-faq-card">
        <h2 className="submit-faq-heading">Send an updated FAQ</h2>
        <p className="submit-faq-intro">
          Upload your filled-in FAQ document. Each upload is kept as a new
          version — submit again whenever you have answers to add, and the
          latest document replaces the earlier one on your FAQ page.
        </p>
        {faqSummary.template && (
          <p className="submit-faq-intro">
            Need the template?{' '}
            <a href={faqSummary.template.file} download={faqSummary.template.downloadName}>
              Download it here
            </a>
            . The steps for filling it in are under Instructions on the Overview tab.
          </p>
        )}

        <form onSubmit={handleSubmit} className="submit-faq-form">
          <div className="submit-faq-field">
            <label htmlFor="faq-name">
              FAQ name <span className="submit-faq-required">(required)</span>
            </label>
            <input
              id="faq-name"
              type="text"
              value={faqName}
              onChange={(e) => setFaqName(e.target.value)}
              placeholder="e.g. Outdoor Lighting FAQ"
              maxLength={255}
              required
            />
          </div>

          <div className="submit-faq-field">
            <label htmlFor="faq-version">Version</label>
            <input
              id="faq-version"
              type="text"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="e.g. v2"
              maxLength={64}
            />
            <p className="submit-faq-hint">
              Optional. The template asks you to increment this with each update.
            </p>
          </div>

          <div className="submit-faq-field">
            <label htmlFor="faq-note">Note</label>
            <textarea
              id="faq-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder="Anything Michael should know about this version — new questions, corrections, timing."
            />
          </div>

          {/* The real <input type="file"> stays in the DOM and keeps focus and
              keyboard activation; the label is the visible drop target. */}
          <div className="submit-faq-field">
            <span className="submit-faq-label-text">
              Document <span className="submit-faq-required">(required)</span>
            </span>
            <div
              className={`submit-faq-dropzone ${dragging ? 'dragging' : ''} ${file ? 'has-file' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <input
                ref={fileInputRef}
                id="faq-file"
                className="submit-faq-file-input"
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(',')}
                onChange={(e) => acceptFile(e.target.files?.[0])}
                aria-describedby="faq-file-hint"
              />
              <label htmlFor="faq-file" className="submit-faq-dropzone-label">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span className="submit-faq-dropzone-main">
                  {file ? file.name : 'Choose a file or drag it here'}
                </span>
                <span className="submit-faq-dropzone-sub">
                  {file ? formatSize(file.size) : 'Word or PDF, up to 15 MB'}
                </span>
              </label>
            </div>
            <p className="submit-faq-hint" id="faq-file-hint">
              Accepted formats: {ACCEPTED_EXTENSIONS.join(', ')}.
            </p>
          </div>

          {error && <p className="submit-faq-error" role="alert">{error}</p>}
          <p className="submit-faq-status" role="status" aria-live="polite">{status}</p>

          <button type="submit" className="submit-faq-btn" disabled={uploading}>
            {uploading ? 'Submitting…' : 'Submit FAQ'}
          </button>
        </form>
      </section>

      <section className="submit-faq-card" aria-labelledby="submit-faq-history-heading">
        <h2 className="submit-faq-heading" id="submit-faq-history-heading">
          {isAdmin ? 'All submissions' : 'Your submissions'}
        </h2>

        {loadingList ? (
          <p className="submit-faq-empty">Loading…</p>
        ) : submissions.length === 0 ? (
          <p className="submit-faq-empty">
            {isAdmin
              ? 'No FAQ documents have been submitted yet.'
              : 'You have not submitted anything yet. Your uploads will be listed here.'}
          </p>
        ) : (
          <ul className="submit-faq-list">
            {submissions.map((s) => (
              <li key={s.id} className="submit-faq-item">
                <div className="submit-faq-item-main">
                  <p className="submit-faq-item-title">
                    {s.faq_name}
                    {s.version && <span className="submit-faq-version">{s.version}</span>}
                  </p>
                  <p className="submit-faq-item-meta">
                    {formatWhen(s.created_at)} · {formatSize(s.byte_size)}
                    {isAdmin && s.user_id !== user?.id && (
                      <> · from {s.submitter_name || s.submitter_email}</>
                    )}
                  </p>
                  {s.note && <p className="submit-faq-item-note">{s.note}</p>}
                </div>
                <div className="submit-faq-item-actions">
                  <a
                    className="submit-faq-download"
                    href={`/api/faq-submissions/${s.id}/file`}
                    aria-label={`Download ${s.filename}`}
                  >
                    {s.filename}
                  </a>
                  {isAdmin && (
                    <button
                      type="button"
                      className="submit-faq-delete"
                      onClick={() => handleDelete(s)}
                      aria-label={`Delete ${s.filename}`}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

export default SubmitFaqPanel;
